// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import { IInput, InputType, InputUnit } from "parameter";
import { isSet, roundToMinDigits } from "utils";

export enum IOState {
  OFF,
  ON,
  UNKNOWN,
  OFFLINE,
}

const DIGITS_TO_SHOW = 3;

class Input {
  readonly parameter: IInput;
  readonly rawValue: number | undefined;
  readonly state: IOState;
  private linxTimestamp: number;

  constructor(parameter: IInput, rawData: number | undefined, linxTimestamp: number) {
    this.parameter = parameter;
    this.linxTimestamp = linxTimestamp;
    this.rawValue = rawData;
    this.state = this.deriveState();
  }

  public isOffline(): boolean {
    return this.state === IOState.OFFLINE || !isSet(this.rawValue) || isNaN(this.rawValue);
  }

  public getValue(): number | Date | boolean | undefined {
    if (this.isOffline()) {
      return undefined;
    }
    // just a shortcut
    const data = this.rawValue;

    // set value depending on data type
    if (this.parameter.type === InputType.SWITCH) {
      return data ? true : false;
    }
    if (this.parameter.type === InputType.ANALOG) {
      // analog data type
      if (this.parameter.unit === "date") {
        // date IO
        if (data) {
          const date = new Date(
            Math.floor(data / 10000) + 1900,
            (Math.floor(data / 100) % 100) - 1,
            data % 100,
          );
          return date;
        }
      }
      if (this.parameter.unit === "time") {
        // time IO in s
        return data;
      }
      if (this.parameter.unit === "timestamp") {
        // timestamp
        if (data) {
          // value is scaled in minutes since 2015
          // timestamp(1.1.2015) = 1420070400
          const unixEpoch = data * 60 + 1420070400;
          // create javascript Date object based on the timestamp
          const date = new Date(unixEpoch * 1000); // Date expects milliseconds
          return date;
        }
      }
    }
    // if we did not explicitely state text we use the same as in value
    return this.rawValue;
  }

  public getText(): string {
    if (this.isOffline()) {
      return "-";
    }
    const data = this.getValue();
    // set value depending on data type
    if (data === true) return "On";
    if (data === false) return "Off";

    if (this.parameter.type === InputType.ANALOG) {
      // analog data type
      if (this.parameter.unit === "date") {
        // date IO
        const date = data as Date;
        if (date) {
          return `${date.getFullYear()}-${`0${date.getMonth() + 1}`.substr(
            -2,
          )}-${`0${date.getDate()}`.substr(-2)}`;
        }
      }
      if (this.parameter.unit === "time") {
        // time IO
        const time = data as number;
        return `${`0${Math.floor(time / 3600)}`.substr(-2)}:${`0${
          Math.floor(time / 60) % 60
        }`.substr(-2)}`;
      }
      if (this.parameter.unit === "timestamp") {
        const timestamp = data as Date;
        if (timestamp instanceof Date && !isNaN(timestamp.getTime())) {
          let dateString = `${timestamp.getFullYear()}-${`0${timestamp.getMonth() + 1}`.substr(
            -2,
          )}-${`0${timestamp.getDate()}`.substr(-2)}`;
          const timeString = `${`0${Math.floor(timestamp.getHours())}`.substr(
            -2,
          )}:${`0${timestamp.getMinutes()}`.substr(-2)}`;
          //const isoDate = dateString + "T" + timeString;
          // short string
          dateString = `${`0${timestamp.getDate()}`.substr(-2)}.${`0${
            timestamp.getMonth() + 1
          }`.substr(-2)}.`;
          return `${dateString} ${timeString}`;
        }
      }
    }
    // if we did not explicitely state text we use the same as in value
    let value = `${roundToMinDigits(this.rawValue, DIGITS_TO_SHOW)}`;
    if (this.parameter.unit) {
      value += ` ${this.parameter.unit}`;
    }
    return value;
  }

  private deriveState() {
    // check for invalid data
    if (!isSet(this.rawValue) || isNaN(this.rawValue)) {
      return IOState.UNKNOWN;
    }
    // just a shortcut
    const data = this.rawValue;

    // set value depending on data type
    if (this.parameter.type !== InputType.ANALOG) {
      // binary IO
      if (data) return IOState.ON;
      return IOState.OFF;
    }

    // analog data type
    if (this.parameter.unit === "date") {
      // date IO
      const date = new Date(
        Math.floor(data / 10000) + 1900,
        (Math.floor(data / 100) % 100) - 1,
        (data % 100) + 1,
      ); // midnight NEXT day
      // current date > parameter date?
      return this.linxTimestamp > date.getTime() ? IOState.ON : IOState.OFF;
    }
    if (this.parameter.unit === "time") {
      // time IO
      return data < (this.linxTimestamp % 24) * 3600 ? IOState.ON : IOState.OFF;
    }
    if (this.parameter.unit === "timestamp") {
      // timestamp
      if (data) {
        // value is scaled in minutes since 2015
        // timestamp(1.1.2015) = 1420070400
        const unixEpoch = data * 60 + 1420070400;
        return unixEpoch < this.linxTimestamp ? IOState.ON : IOState.OFF;
      }
      return IOState.UNKNOWN;
    }
    // other data types: state none
    return IOState.UNKNOWN;
  }
}

export default Input;
